import React, { useEffect, useState } from 'react'
import classes from './styles.module.scss'
import { useKato } from '../../../contexts/kato.context'
import KatoSelects from '../components/kato-selects.component'
import Field from '../../../components/field/field.component'
import { isEmpty, keepCapitalized, onlyLetters, t } from '../../../helpers/utils'
import { GeneralInfoStatus, IChildInfo, IGeneralInfoDetails } from '../../../types/kato.types'
import { useTranslation } from 'react-i18next'
import { getImageById } from '../../../requests/public.request'
import { useShared } from '../../../contexts/shared.context'
import { deleteImage, updateGeneralInfoData, uploadPersonsImage } from '../../../requests/supervisor.request'
import { PenIcon, PlusIcon, XIcon } from '../../../assets/icons'
import ImageGrid from '../components/image-grid.component'
import FooterComponent from '../components/footer.component'
import { toast } from 'react-toastify'

const baseClass = 'general-info-tab'
const translationKey = 'admin.general-info'



const fieldKeys: {
  key: keyof IGeneralInfoDetails,
  type: 'date' | 'number' | 'text',
}[] = [
    { key: 'date', type: 'date' },
    { key: 'population', type: 'number' },
    { key: 'populationDynamic', type: 'number' },
    { key: 'avgSalary', type: 'number' },
    { key: 'area', type: 'number' },
  ];

const GeneralInfoTab = () => {
  const { katoGeneralInfo, updatedGeneralInfo } = useKato();
  const { generalInfo, childInfoList, imageIds, status } = katoGeneralInfo || {};
  const { i18n: { language } } = useTranslation();
  const { selectedInfoYear } = useShared();

  const [errors, setErrors] = useState<Record<string, boolean>>({});

  const [formData, setFormData] = useState<{
    generalInfo: IGeneralInfoDetails;
    childInfoList: IChildInfo[];
    imageIds: number[];
    status: GeneralInfoStatus;
  }>({
    generalInfo: {} as IGeneralInfoDetails,
    childInfoList: [],
    imageIds: [],
    status,
  });

  const handleFieldChange = (key: keyof IGeneralInfoDetails, value: string | number) => {
    setFormData(prev => ({ ...prev, generalInfo: { ...prev.generalInfo, [key]: value } }));
  };

  const handleUploadPersonsImage = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const file = e.target.files?.[0];
      if (file) {
        uploadPersonsImage(katoGeneralInfo.kato, file, selectedInfoYear).then(() => {
          updatedGeneralInfo()
        })
      }
    };
  }

  const handleDeleteImage = (imageId: number) => {
    deleteImage(imageId).then(() => {
      updatedGeneralInfo();
    })
  };

  const handleSave = () => {
    if (validateFields()) {
      const data = {
        generalInfo: formData.generalInfo,
        childInfoList: formData.childInfoList,
        status: formData.status,
      }
      updateGeneralInfoData(
        katoGeneralInfo.kato,
        data,
        selectedInfoYear
      ).then(() => {
        updatedGeneralInfo();
        toast.success(t(`admin.success`, language));
      });
    }
  }

  const validateFields = () => {
    const newErrors: Record<string, boolean> = {};
    fieldKeys.forEach(({ key }) => {
      if (isEmpty(formData.generalInfo[key])) {
        newErrors[key] = true;
      } else {
        newErrors[key] = false;
      }
    });
    setErrors(newErrors);
    return Object.values(newErrors).every(error => !error);
  };

  const renderFields = (lang: 'ru' | 'kz') => {
    const suffix = lang === 'ru' ? 'Ru' : 'Kz';
    return (
      <div className={classes['fields-container']}>

        {/* Начальные поля */}
        {fieldKeys.map(({ key, type }) => (
          <Field
            key={key}
            type={type}
            value={formData.generalInfo[key] || ''}
            onChange={(value) => handleFieldChange(key, value)}
            label={t(`${translationKey}.${key}`, lang)}
            required
            error={errors[key]}
          />
        ))}

        {/* Кол-во населения нижних уровней */}
        <div className={classes[`${baseClass}__child-info`]}>
          <div className={classes[`${baseClass}__child-info__header`]}>
            <span>{t(`${translationKey}.child-info.name`, lang)}</span>
            <span>{t(`${translationKey}.child-info.population`, lang)}</span>
          </div>
          {formData.childInfoList.map((child, index) => (
            <Field
              key={index}
              type='number'
              value={child.population || 0}
              onChange={(value) => {
                const updatedChildInfoList = [...formData.childInfoList];
                updatedChildInfoList[index].population = +value;
                setFormData(prev => ({ ...prev, childInfoList: updatedChildInfoList }));
              }}
              label={child[`name${suffix}`] || ''}
              fieldType='row'
            />
          ))}
        </div>
        {/* Инфо об акиме */}
        <div className={classes[`${baseClass}__akim-info`]}>
          <span>{t(`${translationKey}.akim-info.title`, lang)}</span>

          <Field
            type='text'
            value={formData.generalInfo[`akimName${suffix}`] || ''}
            onChange={(value) => {
              const val = keepCapitalized(onlyLetters(value.toString()));
              handleFieldChange(`akimName${suffix}`, val);
            }}
            label={t(`${translationKey}.akim-info.name`, lang)}
            required
            error={errors[`akimName${suffix}`]}
          />
          <Field
            type='text'
            value={formData.generalInfo[lang === 'ru' ? 'akimPositionRu' : 'akimPositionKz'] || ''}
            onChange={(value) => {
              const val = onlyLetters(value.toString());
              handleFieldChange(`akimPosition${suffix}`, val);
            }}
            label={t(`${translationKey}.akim-info.position`, lang)}
            required
            error={errors[`akimPosition${suffix}`]}
          />

          <div className={classes[`${baseClass}__akim-info__contacts`]}>
            <Field
              type='date'
              value={formData.generalInfo.akimInPositionFrom || ''}
              onChange={(value) => handleFieldChange('akimInPositionFrom', value)}
              label={t(`${translationKey}.akim-info.in-position-from`, lang)}
              required
              error={errors[`akimInPositionFrom`]}
            />

            <Field
              type='number'
              value={formData.generalInfo.akimPhone || ''}
              onChange={(value) => handleFieldChange('akimPhone', value)}
              label={t(`${translationKey}.akim-info.phone`, lang)}
              required
              error={errors[`akimPhone`]}
            />
          </div>

          {
            lang === language && <div className={classes[`${baseClass}__akim-info__photo`]}>
              <div>{t(`${translationKey}.akim-info.photo`, lang)}</div>
              {
                formData.generalInfo.akimImageId
                  ? <>
                    <div className={classes[`${baseClass}__akim-info__photo__image`]}>
                      <div className={classes[`${baseClass}__akim-info__photo__image-container`]}>
                        <img src={getImageById(formData.generalInfo.akimImageId)} alt="фото Акима" />
                      </div>

                      <div className={classes[`${baseClass}__akim-info__photo__actions`]}>
                        <div className={classes[`${baseClass}__akim-info__photo__actions__edit`]}>
                          <label htmlFor="edit-photo" className={classes[`${baseClass}__edit`]}>
                            <input id='edit-photo' type="file" onChange={(e) => handleUploadPersonsImage(e)} />
                            <span> <PenIcon /> {t(`${translationKey}.akim-info.change-photo`, lang)}</span>
                          </label>
                        </div>
                        <span className={classes[`${baseClass}__akim-info__photo__actions__delete`]} onClick={() => handleDeleteImage(formData.generalInfo.akimImageId)}>
                          <XIcon /> {t(`actions.delete`, lang)}
                        </span>
                      </div>
                    </div>
                  </>
                  : <div className={classes[`${baseClass}__akim-info__photo__actions`]}>
                    <div className={classes[`${baseClass}__akim-info__photo__actions__upload`]} >
                      <label htmlFor="upload-photo">
                        <span><PlusIcon /> {t(`${translationKey}.akim-info.upload-photo`, lang)}</span>
                        <input id="upload-photo" type="file" onChange={(e) => handleUploadPersonsImage(e)} />
                      </label>
                    </div>
                  </div>
              }
            </div>
          }
        </div>
      </div>
    )
  };

  useEffect(() => {
    if (generalInfo && katoGeneralInfo.kato) {
      setFormData({
        generalInfo,
        childInfoList,
        imageIds,
        status,
      });
    }
  }, [generalInfo, katoGeneralInfo.kato, selectedInfoYear]);


  if (!generalInfo || !katoGeneralInfo.kato) return null;

  return (
    <div className={`${baseClass} ${classes[baseClass]}`}>
      <div className={classes['grid']}>
        <KatoSelects lang='ru' />
        <KatoSelects lang='kz' />
        {renderFields(language as 'ru' | 'kz')}
        {renderFields(language === 'ru' ? 'kz' : 'ru')}

        {/* Грид с изображениями */}

      </div>
      <ImageGrid
        kato={katoGeneralInfo.kato}
        imageIds={formData.imageIds || []}
        type='general-info'
      />

      <FooterComponent
        onSave={handleSave}
        status={formData.status}
        validateFields={validateFields}
      />
    </div>
  )
}

export default GeneralInfoTab